import { useState, useEffect } from 'react'
import { API_URL } from '@/config/constants'

export interface TimeSlot {
  period: number
  start: string
  end: string
  isBreak: boolean
}

export const useTimeSlots = () => {
  const [timeSlotData, setTimeSlotData] = useState<TimeSlot[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchTimeSlots = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await fetch(`${API_URL}/timetable/etablissement`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch timetable data: ${response.statusText}`
        )
      }

      const data = await response.json()

      if (data.inputData?.timeSlots) {
        const timeSlots: TimeSlot[] = []
        const days = Object.keys(data.inputData.timeSlots)

        // Use a Map to store unique time slots
        const timeSlotsMap = new Map<
          string,
          { time: string; isBreak: boolean; start: string; end: string }
        >()

        // Process each day's time slots
        days.forEach((day) => {
          const dayData = data.inputData.timeSlots[day]

          // Add study times (not breaks)
          dayData.studyTimes.forEach((time: string) => {
            if (!timeSlotsMap.has(time)) {
              timeSlotsMap.set(time, {
                time,
                isBreak: false,
                start: time.split(' => ')[0],
                end: time.split(' => ')[1],
              })
            }
          })

          // Add rest time (as break)
          if (dayData.restTime) {
            timeSlotsMap.set(dayData.restTime, {
              time: dayData.restTime,
              isBreak: true,
              start: dayData.restTime.split(' => ')[0],
              end: dayData.restTime.split(' => ')[1],
            })
          }
        })

        // Convert time strings to minutes for proper sorting
        const timeToMinutes = (timeStr: string): number => {
          const [hours, minutes] = timeStr.split(':').map(Number)
          return (hours || 0) * 60 + (minutes || 0)
        }

        // Sort all time slots by their start time
        const sortedSlots = Array.from(timeSlotsMap.values()).sort(
          (a, b) => timeToMinutes(a.start) - timeToMinutes(b.start)
        )

        // Assign period numbers based on sorted order
        sortedSlots.forEach((slot, index) => {
          timeSlots.push({
            period: index + 1,
            start: slot.start,
            end: slot.end,
            isBreak: slot.isBreak,
          })
        })

        setTimeSlotData(timeSlots)
      }
    } catch (err) {
      // console.error('Error in fetchTimeSlots:', err)
      setError(
        err instanceof Error ? err : new Error('An unknown error occurred')
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTimeSlots()
  }, [])

  const refetch = () => {
    fetchTimeSlots()
  }

  return {
    timeSlotData,
    isLoading,
    error,
    refetch,
  }
}

// export  const getDays = () => {
//   const schedule = getSelectedClassSchedule()

//   // Define the standard week days that should always be shown
//   const standardDays = [
//     'Monday',
//     'Tuesday',
//     'Wednesday',
//     'Thursday',
//     'Friday',
//     'Saturday',
//     'Sunday',
//   ]

//   if (!schedule) return standardDays

//   console.log('Schedule for selected class:', schedule)

//   // Get days from the schedule
//   const scheduleDays = Object.keys(schedule).filter((key) => {
//     // Skip the 'class' property
//     if (key === 'class') return false

//     // Include all day properties, even if they're empty arrays
//     return true
//   })

//   // Combine standard days with any additional days from the schedule
//   const allDays = new Set([...standardDays, ...scheduleDays])

//   // Define the correct day order - standard week order
//   const dayOrder = [
//     'Monday',
//     'Tuesday',
//     'Wednesday',
//     'Thursday',
//     'Friday',
//     'Saturday',
//     'Sunday',
//   ]

//   // Sort the days according to the defined order
//   const sortedDays = Array.from(allDays).sort((a, b) => {
//     const indexA = dayOrder.indexOf(a)
//     const indexB = dayOrder.indexOf(b)

//     // If both days are in the order array, sort by their position
//     if (indexA !== -1 && indexB !== -1) {
//       return indexA - indexB
//     }

//     // If only one day is in the order array, prioritize it
//     if (indexA !== -1) return -1
//     if (indexB !== -1) return 1

//     // If neither day is in the order array, maintain alphabetical order
//     return a.localeCompare(b)
//   })

//   console.log('Days for timetable (sorted):', sortedDays)
//   return sortedDays
// }
